/**
 * Payroll Reconciliation Card for Accountant Report View
 * Displays payroll reconciliation data and variance analysis
 */

import React from 'react';
import CommentsSection from '../CommentsSection';

interface PayrollReconciliation {
  pl_total_2022?: number;
  variance_pct?: number;
  reconciled?: boolean;
  recommendation?: string;
}

interface PayrollReconciliationCardProps {
  payrollReconciliation: PayrollReconciliation;
  reportId: string;
  canComment: boolean;
  getAuthHeader: () => { Authorization: string };
  formatCurrency: (amount?: number) => string;
  formatPercent: (value?: number) => string;
}

export default function PayrollReconciliationCard({
  payrollReconciliation,
  reportId,
  canComment,
  getAuthHeader,
  formatCurrency,
  formatPercent
}: PayrollReconciliationCardProps) {
  if (!payrollReconciliation) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <span className="text-2xl mr-2">👥</span>
        Payroll Reconciliation
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-blue-50 rounded-lg p-4">
          <p className="text-sm text-blue-800 mb-2 font-medium">P&L Monthly Total (2022)</p>
          <p className="text-2xl font-bold text-blue-900">
            {formatCurrency(payrollReconciliation.pl_total_2022)}
          </p>
        </div>
        <div className="bg-purple-50 rounded-lg p-4">
          <p className="text-sm text-purple-800 mb-2 font-medium">Variance</p>
          <p className="text-2xl font-bold text-purple-900">
            {formatPercent(payrollReconciliation.variance_pct)}
          </p>
          <p className={`text-sm mt-2 font-medium ${payrollReconciliation.reconciled ? 'text-green-700' : 'text-red-700'}`}>
            {payrollReconciliation.reconciled ? '✓ Reconciled' : '⚠ Variance Detected'}
          </p>
        </div>
      </div>
      {payrollReconciliation.recommendation && (
        <div className="mt-4 bg-gray-50 rounded-lg p-4">
          <p className="text-sm text-gray-700">{payrollReconciliation.recommendation}</p>
        </div>
      )}
      
      {canComment && (
        <CommentsSection
          reportId={reportId}
          section="payroll_reconciliation"
          sectionTitle="Payroll Reconciliation"
          getAuthHeader={getAuthHeader}
          canComment={true}
        />
      )}
    </div>
  );
}
